/**
 * SubscriptionService
 * Manages subscription information and operations
 * 
 * Requirements: 6.1, 6.4, 7.2
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import { AuthService } from './AuthService';
import config from '../config';
import type { Subscription, SubscriptionTier } from '../types';

export class SubscriptionService {
  private axiosInstance: AxiosInstance;
  private authService: AuthService;

  constructor() {
    this.authService = AuthService.getInstance();

    // Create axios instance with base configuration
    this.axiosInstance = axios.create({
      baseURL: config.apiBaseUrl,
      timeout: 10000, // 10 second timeout
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include JWT token
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        try {
          const token = await this.authService.getValidAccessToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch (error) {
          console.error('Failed to get access token:', error);
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        if (error.response?.status === 401) {
          console.error('Authentication failed');
          await this.authService.logout();
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Get current subscription information
   * Requirement 6.1
   * 
   * @param invitationCode Optional invitation code for new users
   * @returns Promise resolving to subscription data
   * @throws Error if fetch fails
   */
  async getSubscription(invitationCode?: string): Promise<Subscription> {
    try {
      // Check for invitation code in localStorage if not provided
      if (!invitationCode) {
        invitationCode = localStorage.getItem('invitation_code') || undefined;
        // Clean up after use
        if (invitationCode) {
          localStorage.removeItem('invitation_code');
        }
      }

      // Add timestamp to prevent caching
      const timestamp = Date.now();
      let url = `/api/subscription?_t=${timestamp}`;
      
      // Add invitation code if present
      if (invitationCode) {
        url += `&invitation_code=${encodeURIComponent(invitationCode)}`;
        console.log('[SubscriptionService] Including invitation code in subscription request');
      }

      const response = await this.axiosInstance.get<Subscription>(
        url,
        {
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        }
      );

      console.log('[SubscriptionService] Fetched subscription:', {
        type: response.data.subscription_type,
        credits: response.data.credits_remaining,
        timestamp: new Date().toISOString()
      });
      return response.data;
    } catch (error) {
      console.error('[SubscriptionService] Failed to fetch subscription:', error);
      throw this.handleError(error, 'Failed to fetch subscription information');
    }
  }

  /**
   * Get available subscription tiers
   * Requirement 7.2
   * 
   * @returns Promise resolving to array of subscription tiers
   * @throws Error if fetch fails
   */
  async getTiers(): Promise<SubscriptionTier[]> {
    try {
      const response = await this.axiosInstance.get<SubscriptionTier[]>(
        '/api/subscription/tiers'
      );

      console.log('Fetched subscription tiers:', response.data.length);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch subscription tiers:', error);
      throw this.handleError(error, 'Failed to fetch subscription tiers');
    }
  }

  /**
   * Create Stripe checkout session for subscription upgrade
   * Requirement 7.2
   * 
   * @param tier - The tier name to upgrade to
   * @param successUrl - URL to redirect to after successful payment
   * @param cancelUrl - URL to redirect to if payment is cancelled
   * @returns Promise resolving to checkout session with URL
   * @throws Error if creation fails
   */
  async createCheckoutSession(
    tier: string,
    successUrl: string,
    cancelUrl: string
  ): Promise<{ checkout_url: string }> {
    try {
      const response = await this.axiosInstance.post<{ checkout_url: string }>(
        '/api/subscription/checkout',
        {
          tier,
          success_url: successUrl,
          cancel_url: cancelUrl,
        }
      );

      console.log('Created checkout session for tier:', tier);
      return response.data;
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      throw this.handleError(error, 'Failed to create checkout session');
    }
  }

  /**
   * Create Stripe checkout session for purchasing additional credits
   * 
   * @param quantity - Number of credits to purchase
   * @param successUrl - URL to redirect to after successful payment
   * @param cancelUrl - URL to redirect to if payment is cancelled
   * @returns Promise resolving to checkout session with URL
   * @throws Error if creation fails
   */
  async purchaseCredits(
    quantity: number,
    successUrl: string,
    cancelUrl: string
  ): Promise<{ checkout_url: string }> {
    try {
      console.log('Calling credit purchase API:', { quantity, successUrl, cancelUrl });
      
      const response = await this.axiosInstance.post<{ checkout_url: string }>(
        '/api/subscription/credits/purchase',
        {
          quantity,
          success_url: successUrl,
          cancel_url: cancelUrl,
        }
      );

      console.log('Credit purchase API response:', response.data);
      
      if (!response.data || !response.data.checkout_url) {
        throw new Error('Invalid response from server: missing checkout_url');
      }

      return response.data;
    } catch (error) {
      console.error('Failed to create credit purchase session:', error);
      if (axios.isAxiosError(error) && error.response) {
        console.error('Error response:', error.response.data);
      }
      throw this.handleError(error, 'Failed to create credit purchase session');
    }
  }

  /**
   * Cancel active subscription
   * Requirement 6.4
   * 
   * @returns Promise resolving when cancellation is complete
   * @throws Error if cancellation fails
   */
  async cancelSubscription(): Promise<void> {
    try {
      await this.axiosInstance.post('/api/subscription/cancel');

      console.log('Subscription cancelled successfully');
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      throw this.handleError(error, 'Failed to cancel subscription');
    }
  }

  /**
   * Handle errors and convert to user-friendly messages
   * 
   * @param error - The error to handle
   * @param defaultMessage - Default message if error cannot be parsed
   * @returns Error with user-friendly message
   */
  private handleError(error: unknown, defaultMessage: string): Error {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;

      if (axiosError.code === 'ECONNABORTED') {
        return new Error('Request timeout. Please check your connection.');
      }

      if (axiosError.code === 'ERR_NETWORK') {
        return new Error('Network error. Please check your connection.');
      }

      if (axiosError.response) {
        const status = axiosError.response.status;
        const data = axiosError.response.data as any;

        switch (status) {
          case 400:
            // Try to extract error message from response
            if (data?.error?.message) {
              return new Error(data.error.message);
            }
            if (data?.detail) {
              return new Error(data.detail);
            }
            return new Error('Invalid request. Please check your input.');
          case 401:
            return new Error('Authentication failed. Please log in again.');
          case 403:
            return new Error('You do not have permission to perform this action.');
          case 404:
            return new Error('Subscription not found.');
          case 409:
            // Conflict - e.g., already subscribed to this tier
            if (data?.detail) {
              return new Error(data.detail);
            }
            return new Error('Subscription conflict. You may already be subscribed to this tier.');
          case 429:
            return new Error('Too many requests. Please wait a moment.');
          case 500:
          case 502:
          case 503:
            if (data?.error?.message) {
              return new Error(data.error.message);
            }
            return new Error('Server error. Please try again later.');
          default:
            if (data?.error?.message) {
              return new Error(data.error.message);
            }
            if (data?.detail) {
              return new Error(data.detail);
            }
            return new Error(`${defaultMessage} (Status: ${status})`);
        }
      }

      return new Error('Network error. Please check your connection.');
    }

    if (error instanceof Error) {
      return error;
    }

    return new Error(defaultMessage);
  }
}

export default SubscriptionService;
